import type { ReactNode } from 'react';
import { createContext, useContext, useMemo } from 'react';
import { ImageSourceResolverContext } from '../../../shared/ImageSourceResolver';
import {
  assetIdFromDeviceAssetReference,
  createDeviceAssetReference,
  isDeviceAssetReference,
  migrateLegacyDeviceMedia,
  projectDeviceMedia,
  stableDeviceImageId,
} from './deviceMedia';
import type { CpqState, DeviceCatalogRecord, DeviceImageAsset } from './types';

export const DEVICE_ASSET_DB_NAME = 'cpq-v2-device-media';
export const DEVICE_ASSET_DB_VERSION = 1;
export const DEVICE_ASSET_STORE_NAME = 'assets';

export type DeviceAssetStoreKind = 'indexeddb' | 'memory-test';

export type DeviceAssetErrorCode =
  | 'unavailable'
  | 'read-failed'
  | 'write-failed'
  | 'delete-failed'
  | 'migration-failed';

export class DeviceAssetStorageError extends Error {
  readonly code: DeviceAssetErrorCode;

  constructor(code: DeviceAssetErrorCode, message: string, cause?: unknown) {
    super(message, { cause });
    this.name = 'DeviceAssetStorageError';
    this.code = code;
  }
}

export interface DeviceAssetRecord {
  assetId: string;
  deviceId: string;
  name: string;
  mimeType: string;
  size: number;
  blob: Blob;
  createdAt: string;
}

export interface StoredDeviceAsset {
  assetId: string;
  objectUrl: string;
  name: string;
  mimeType: string;
  size: number;
}

export interface PutDeviceAssetInput {
  deviceId: string;
  name: string;
  mimeType: string;
  size: number;
  blob: Blob;
  assetId?: string;
}

export interface DeviceAssetStore {
  readonly kind: DeviceAssetStoreKind;
  put(input: PutDeviceAssetInput): Promise<StoredDeviceAsset>;
  resolve(assetId: string): Promise<string | null>;
  delete(assetId: string): Promise<void>;
  deleteMany(assetIds: Iterable<string>): Promise<void>;
  reconcileDevice(
    deviceId: string,
    keepAssetIds: ReadonlySet<string>,
  ): Promise<void>;
  clear(): Promise<void>;
}

function hasTestRuntime() {
  return (
    typeof process !== 'undefined' &&
    (process.env.NODE_ENV === 'test' || process.env.VITEST === 'true')
  );
}
function assetError(
  code: DeviceAssetErrorCode,
  message: string,
  cause?: unknown,
) {
  return new DeviceAssetStorageError(code, message, cause);
}

function createAssetId() {
  if (
    typeof crypto !== 'undefined' &&
    typeof crypto.randomUUID === 'function'
  ) {
    return `ASSET-${crypto.randomUUID()}`;
  }
  return `ASSET-${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
}

function createObjectUrl(blob: Blob, assetId: string) {
  if (typeof URL !== 'undefined' && typeof URL.createObjectURL === 'function') {
    const url = URL.createObjectURL(blob);
    if (typeof url === 'string' && url) return url;
  }
  // happy-dom does not implement Blob URLs. This test-only display reference
  // is never written back to LocalStorage because the image carries assetId.
  return `data:image/svg+xml,${encodeURIComponent(
    `<svg xmlns="http://www.w3.org/2000/svg" width="2" height="2"><rect width="2" height="2" fill="#d8cedf"/><title>${assetId}</title></svg>`,
  )}`;
}

function revokeObjectUrl(url: string) {
  if (
    url.startsWith('blob:') &&
    typeof URL !== 'undefined' &&
    typeof URL.revokeObjectURL === 'function'
  ) {
    URL.revokeObjectURL(url);
  }
}

function storedAsset(
  record: DeviceAssetRecord,
  objectUrl: string,
): StoredDeviceAsset {
  return {
    assetId: record.assetId,
    objectUrl,
    name: record.name,
    mimeType: record.mimeType,
    size: record.size,
  };
}

export function createMemoryDeviceAssetStore(): DeviceAssetStore {
  const records = new Map<string, DeviceAssetRecord>();
  const objectUrls = new Map<string, string>();

  const objectUrlFor = (record: DeviceAssetRecord) => {
    const current = objectUrls.get(record.assetId);
    if (current) return current;
    const next = createObjectUrl(record.blob, record.assetId);
    objectUrls.set(record.assetId, next);
    return next;
  };

  return {
    kind: 'memory-test',
    async put(input) {
      const record: DeviceAssetRecord = {
        assetId: input.assetId ?? createAssetId(),
        deviceId: input.deviceId,
        name: input.name,
        mimeType: input.mimeType,
        size: input.size,
        blob: input.blob,
        createdAt: new Date().toISOString(),
      };
      records.set(record.assetId, record);
      return storedAsset(record, objectUrlFor(record));
    },
    async resolve(assetId) {
      const record = records.get(assetId);
      return record ? objectUrlFor(record) : null;
    },
    async delete(assetId) {
      const url = objectUrls.get(assetId);
      if (url) revokeObjectUrl(url);
      objectUrls.delete(assetId);
      records.delete(assetId);
    },
    async deleteMany(assetIds) {
      for (const assetId of assetIds) await this.delete(assetId);
    },
    async reconcileDevice(deviceId, keepAssetIds) {
      for (const record of records.values()) {
        if (record.deviceId === deviceId && !keepAssetIds.has(record.assetId)) {
          await this.delete(record.assetId);
        }
      }
    },
    async clear() {
      await this.deleteMany([...records.keys()]);
    },
  };
}

interface IndexedDbAssetRecord extends DeviceAssetRecord {
  assetId: string;
}

function requestResult<T>(request: IDBRequest<T>) {
  return new Promise<T>((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

function transactionDone(transaction: IDBTransaction) {
  return new Promise<void>((resolve, reject) => {
    transaction.oncomplete = () => resolve();
    transaction.onerror = () => reject(transaction.error);
    transaction.onabort = () => reject(transaction.error);
  });
}

function openAssetDatabase(factory: IDBFactory) {
  return new Promise<IDBDatabase>((resolve, reject) => {
    const request = factory.open(DEVICE_ASSET_DB_NAME, DEVICE_ASSET_DB_VERSION);
    request.onupgradeneeded = () => {
      const database = request.result;
      if (database.objectStoreNames.contains(DEVICE_ASSET_STORE_NAME)) return;
      const store = database.createObjectStore(DEVICE_ASSET_STORE_NAME, {
        keyPath: 'assetId',
      });
      store.createIndex('by-device-id', 'deviceId', { unique: false });
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

function createIndexedDbDeviceAssetStore(
  factory: IDBFactory,
): DeviceAssetStore {
  let databasePromise: Promise<IDBDatabase> | null = null;
  const objectUrls = new Map<string, string>();

  const database = () => {
    databasePromise ??= openAssetDatabase(factory);
    return databasePromise;
  };
  const objectUrlFor = (record: DeviceAssetRecord) => {
    const current = objectUrls.get(record.assetId);
    if (current) return current;
    const next = createObjectUrl(record.blob, record.assetId);
    objectUrls.set(record.assetId, next);
    return next;
  };
  const read = async (assetId: string) => {
    try {
      const db = await database();
      const transaction = db.transaction(DEVICE_ASSET_STORE_NAME, 'readonly');
      const request = transaction
        .objectStore(DEVICE_ASSET_STORE_NAME)
        .get(assetId);
      const record = await requestResult<IndexedDbAssetRecord | undefined>(
        request,
      );
      await transactionDone(transaction);
      return record ?? null;
    } catch (error) {
      throw assetError(
        'read-failed',
        '浏览器图片资产读取失败，请刷新后重试',
        error,
      );
    }
  };

  return {
    kind: 'indexeddb',
    async put(input) {
      const record: IndexedDbAssetRecord = {
        assetId: input.assetId ?? createAssetId(),
        deviceId: input.deviceId,
        name: input.name,
        mimeType: input.mimeType,
        size: input.size,
        blob: input.blob,
        createdAt: new Date().toISOString(),
      };
      try {
        const db = await database();
        const transaction = db.transaction(
          DEVICE_ASSET_STORE_NAME,
          'readwrite',
        );
        transaction.objectStore(DEVICE_ASSET_STORE_NAME).put(record);
        await transactionDone(transaction);
        return storedAsset(record, objectUrlFor(record));
      } catch (error) {
        throw assetError(
          'write-failed',
          '浏览器图片资产写入失败，本次图片未添加，请重试',
          error,
        );
      }
    },
    async resolve(assetId) {
      const record = await read(assetId);
      return record ? objectUrlFor(record) : null;
    },
    async delete(assetId) {
      try {
        const db = await database();
        const transaction = db.transaction(
          DEVICE_ASSET_STORE_NAME,
          'readwrite',
        );
        transaction.objectStore(DEVICE_ASSET_STORE_NAME).delete(assetId);
        await transactionDone(transaction);
        const url = objectUrls.get(assetId);
        if (url) revokeObjectUrl(url);
        objectUrls.delete(assetId);
      } catch (error) {
        throw assetError(
          'delete-failed',
          '浏览器图片资产删除失败，请重试',
          error,
        );
      }
    },
    async deleteMany(assetIds) {
      for (const assetId of assetIds) await this.delete(assetId);
    },
    async reconcileDevice(deviceId, keepAssetIds) {
      try {
        const db = await database();
        const readTransaction = db.transaction(
          DEVICE_ASSET_STORE_NAME,
          'readonly',
        );
        const records = await requestResult<IndexedDbAssetRecord[]>(
          readTransaction.objectStore(DEVICE_ASSET_STORE_NAME).getAll(),
        );
        await transactionDone(readTransaction);
        const removable = records.filter(
          (record) =>
            record.deviceId === deviceId && !keepAssetIds.has(record.assetId),
        );
        await this.deleteMany(removable.map((record) => record.assetId));
      } catch (error) {
        if (error instanceof DeviceAssetStorageError) throw error;
        throw assetError(
          'delete-failed',
          '浏览器图片资产清理失败，请重试',
          error,
        );
      }
    },
    async clear() {
      try {
        const db = await database();
        const transaction = db.transaction(
          DEVICE_ASSET_STORE_NAME,
          'readwrite',
        );
        transaction.objectStore(DEVICE_ASSET_STORE_NAME).clear();
        await transactionDone(transaction);
        for (const url of objectUrls.values()) revokeObjectUrl(url);
        objectUrls.clear();
      } catch (error) {
        throw assetError(
          'delete-failed',
          '浏览器图片资产清理失败，请重试',
          error,
        );
      }
    },
  };
}

let sharedTestStore: DeviceAssetStore | null = null;

export function createDeviceAssetStore(options?: {
  indexedDBFactory?: IDBFactory;
  allowMemoryFallback?: boolean;
}): DeviceAssetStore {
  const factory =
    options?.indexedDBFactory ??
    (typeof indexedDB !== 'undefined' ? indexedDB : undefined);
  if (factory) return createIndexedDbDeviceAssetStore(factory);
  if (options?.allowMemoryFallback || hasTestRuntime()) {
    sharedTestStore ??= createMemoryDeviceAssetStore();
    return sharedTestStore;
  }
  throw assetError(
    'unavailable',
    '当前浏览器不支持 IndexedDB 图片资产存储，无法可靠添加设备图片',
  );
}

function isDataImageSource(source: string) {
  return /^data:image\//i.test(source);
}

export function blobFromDataImageSource(
  source: string,
  fallbackMimeType: string,
) {
  const match = source.match(/^data:([^;,]+)?((?:;[^,]+)*),(.*)$/s);
  if (!match) return null;
  const mimeType = match[1] || fallbackMimeType || 'application/octet-stream';
  const metadata = match[2] ?? '';
  const payload = match[3] ?? '';
  try {
    if (metadata.includes(';base64')) {
      const binary = atob(payload);
      const bytes = Uint8Array.from(binary, (character) =>
        character.charCodeAt(0),
      );
      return new Blob([bytes], { type: mimeType });
    }
    return new Blob([decodeURIComponent(payload)], { type: mimeType });
  } catch {
    // Legacy tests and old fixtures sometimes used symbolic base64 payloads.
    // Keep migration deterministic and let the image UI report a later load
    // error instead of writing the original data URI back to LocalStorage.
    return new Blob([payload], { type: mimeType });
  }
}

function legacyAssetId(deviceId: string, image: DeviceImageAsset) {
  return `LEGACY-${stableDeviceImageId(deviceId, image.source, image.order).slice(4)}`;
}

function allQuoteItems(state: CpqState) {
  return Object.values(state.quotes).flatMap((quote) => [
    ...quote.items,
    ...(quote.itemAdjustments ?? []).flatMap((adjustment) => [
      adjustment.originalItemSnapshot,
      ...(adjustment.effectiveItemSnapshot
        ? [adjustment.effectiveItemSnapshot]
        : []),
    ]),
  ]);
}

/**
 * Return asset keys present in the catalog state so a failed business-state
 * write can release assets that were staged by the same attempted update.
 */
export function deviceAssetIdsInState(state: CpqState) {
  const assetIds = new Set<string>();
  for (const device of state.catalog.devices) {
    for (const image of device.images) {
      if (image.assetId) assetIds.add(image.assetId);
    }
  }
  return assetIds;
}

function rewriteQuoteSnapshotImage(
  item: {
    deviceId: string;
    deviceFieldSnapshot?: { image?: string };
  },
  sourceToRuntime: ReadonlyMap<string, string>,
  deviceById: ReadonlyMap<string, DeviceCatalogRecord>,
) {
  const image = item.deviceFieldSnapshot?.image;
  if (!image) return;
  const runtimeSource = sourceToRuntime.get(image);
  if (runtimeSource) {
    item.deviceFieldSnapshot = {
      ...item.deviceFieldSnapshot,
      image: runtimeSource,
    };
    return;
  }
  if (isDataImageSource(image)) {
    const deviceSource = deviceById.get(item.deviceId)?.image;
    if (deviceSource) {
      item.deviceFieldSnapshot = {
        ...item.deviceFieldSnapshot,
        image: deviceSource,
      };
    }
  }
}

type RuntimeSnapshotOwner = {
  quoteId: string;
  itemsSnapshot?: Array<{ itemId: string; image?: string }>;
  selectionOptionsSnapshot?: Array<{ itemId: string; image?: string }>;
  quotationListProjection?: {
    fields: Array<{ fieldId: string; presentation?: string }>;
    rows: Array<{
      rowId: string;
      values: Record<string, { value: unknown }>;
    }>;
  };
};

function rewritePublishedSnapshotImages(
  owner: RuntimeSnapshotOwner,
  state: CpqState,
  sourceToRuntime: ReadonlyMap<string, string>,
) {
  const quoteItems = new Map(
    (state.quotes[owner.quoteId]?.items ?? []).map((item) => [
      item.itemId,
      item,
    ]),
  );
  const runtimeImage = (image: string | undefined, itemId: string) => {
    if (!image) return image;
    const resolved = sourceToRuntime.get(image);
    if (resolved && !isDeviceAssetReference(resolved)) return resolved;
    if (!isDeviceAssetReference(image) && !image.startsWith('blob:'))
      return image;
    return quoteItems.get(itemId)?.deviceFieldSnapshot?.image ?? image;
  };
  for (const item of owner.itemsSnapshot ?? []) {
    item.image = runtimeImage(item.image, item.itemId);
  }
  for (const item of owner.selectionOptionsSnapshot ?? []) {
    item.image = runtimeImage(item.image, item.itemId);
  }
  const projection = owner.quotationListProjection;
  const imageFieldIds = new Set(
    (projection?.fields ?? [])
      .filter((field) => field.presentation === 'image')
      .map((field) => field.fieldId),
  );
  for (const row of projection?.rows ?? []) {
    for (const fieldId of imageFieldIds) {
      const cell = row.values[fieldId];
      if (!cell || typeof cell.value !== 'string') continue;
      cell.value = runtimeImage(cell.value, row.rowId);
    }
  }
}

/**
 * Hydrates device asset references before CpqProvider mounts. LocalStorage is
 * never written while this async migration is in flight, so legacy data URIs
 * can be moved into IndexedDB without a transient business-state write.
 */
export async function hydrateDeviceMediaState(
  state: CpqState,
  store: DeviceAssetStore,
): Promise<CpqState> {
  const next = structuredClone(state);
  const sourceToRuntime = new Map<string, string>();
  const devices = next.catalog.devices;

  for (const device of devices) {
    const media = migrateLegacyDeviceMedia(device);
    const images = await Promise.all(
      media.images.map(async (image): Promise<DeviceImageAsset> => {
        const oldSource = image.source;
        let assetId =
          image.assetId ?? assetIdFromDeviceAssetReference(oldSource);
        let source = oldSource;
        if (assetId) {
          const resolved = await store.resolve(assetId);
          source = resolved ?? createDeviceAssetReference(assetId);
        } else if (isDataImageSource(oldSource)) {
          const blob = blobFromDataImageSource(oldSource, image.mimeType);
          if (!blob) {
            throw assetError(
              'migration-failed',
              `历史设备图片「${image.name}」无法迁移到浏览器资产存储`,
            );
          }
          const stored = await store.put({
            assetId: legacyAssetId(device.id, image),
            deviceId: device.id,
            name: image.name,
            mimeType: image.mimeType || blob.type,
            size: image.size || blob.size,
            blob,
          });
          assetId = stored.assetId;
          source = stored.objectUrl;
        }
        if (assetId) {
          sourceToRuntime.set(oldSource, source);
          sourceToRuntime.set(createDeviceAssetReference(assetId), source);
        }
        return { ...image, assetId: assetId ?? undefined, source };
      }),
    );
    const projected = projectDeviceMedia(images, media.primaryImageId);
    const index = devices.findIndex((candidate) => candidate.id === device.id);
    if (index >= 0) {
      devices[index] = { ...device, ...projected };
    }
  }

  const deviceById = new Map(devices.map((device) => [device.id, device]));
  for (const item of allQuoteItems(next)) {
    rewriteQuoteSnapshotImage(item, sourceToRuntime, deviceById);
  }
  for (const share of next.shares) {
    rewritePublishedSnapshotImages(share, next, sourceToRuntime);
  }
  for (const publication of next.publications ?? []) {
    rewritePublishedSnapshotImages(publication, next, sourceToRuntime);
  }
  return next;
}

const DeviceAssetStoreContext = createContext<DeviceAssetStore | null>(null);

export function DeviceAssetStoreProvider({
  store,
  children,
}: {
  store: DeviceAssetStore;
  children: ReactNode;
}) {
  const imageResolver = useMemo(
    () => ({
      canResolve: (source: string) =>
        Boolean(assetIdFromDeviceAssetReference(source)),
      resolve: (source: string) => {
        const assetId = assetIdFromDeviceAssetReference(source);
        return assetId ? store.resolve(assetId) : Promise.resolve(source);
      },
    }),
    [store],
  );
  return (
    <DeviceAssetStoreContext.Provider value={store}>
      <ImageSourceResolverContext.Provider value={imageResolver}>
        {children}
      </ImageSourceResolverContext.Provider>
    </DeviceAssetStoreContext.Provider>
  );
}

export function useDeviceAssetStore() {
  const store = useContext(DeviceAssetStoreContext);
  if (!store) throw new Error('设备图片资产存储未注入');
  return store;
}

export function useOptionalDeviceAssetStore() {
  return useContext(DeviceAssetStoreContext);
}

export function isDeviceAssetStoreUnavailable(error: unknown) {
  return (
    error instanceof DeviceAssetStorageError && error.code === 'unavailable'
  );
}
